import { useState, useCallback } from 'react';
import axios from 'axios';
import { CLIENT_ENDPOINTS } from '../constants/api';
import type {
  ListingClient,
  ListingClientType,
  CreateClientPayload,
  UpdateClientPayload,
} from '../types/ListingTypes';

export type { ListingClient, ListingClientType, CreateClientPayload, UpdateClientPayload } from '../types/ListingTypes';

export interface UseClientDataReturn {
  loading: boolean;
  error: string | null;
  fetchClientTypes: (params?: { listingTypeId?: string; listingTypeName?: string }) => Promise<ListingClientType[]>;
  fetchClientsByListing: (listingId: string) => Promise<ListingClient[]>;
  fetchClient: (id: string) => Promise<ListingClient>;
  createClient: (payload: CreateClientPayload) => Promise<ListingClient>;
  updateClient: (id: string, payload: UpdateClientPayload) => Promise<ListingClient>;
  deleteClient: (id: string) => Promise<void>;
}

const authHeader = () => ({
  Authorization: `Bearer ${sessionStorage.getItem('token')}`,
});

export function useClientData(): UseClientDataReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const withLoading = useCallback(async <T>(fn: () => Promise<T>): Promise<T> => {
    setLoading(true);
    setError(null);
    try {
      return await fn();
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'An unexpected error occurred.';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClientTypes = useCallback(
    (params?: { listingTypeId?: string; listingTypeName?: string }): Promise<ListingClientType[]> =>
      withLoading(async () => {
        const res = await axios.get(CLIENT_ENDPOINTS.CLIENT_TYPES, {
          headers: authHeader(),
          params,
        });
        return res.data || [];
      }),
    [withLoading]
  );

  const fetchClientsByListing = useCallback((listingId: string): Promise<ListingClient[]> =>
    withLoading(async () => {
      const res = await axios.get(CLIENT_ENDPOINTS.BY_LISTING(listingId), {
        headers: authHeader(),
      });
      return res.data || [];
    }), [withLoading]);

  const fetchClient = useCallback((id: string): Promise<ListingClient> =>
    withLoading(async () => {
      const res = await axios.get(CLIENT_ENDPOINTS.BY_ID(id), {
        headers: authHeader(),
      });
      return res.data;
    }), [withLoading]);

  const createClient = useCallback((payload: CreateClientPayload): Promise<ListingClient> =>
    withLoading(async () => {
      const res = await axios.post(CLIENT_ENDPOINTS.CLIENTS, payload, {
        headers: authHeader(),
      });
      return res.data;
    }), [withLoading]);

  const updateClient = useCallback((id: string, payload: UpdateClientPayload): Promise<ListingClient> =>
    withLoading(async () => {
      const res = await axios.put(CLIENT_ENDPOINTS.BY_ID(id), payload, {
        headers: authHeader(),
      });
      return res.data;
    }), [withLoading]);

  const deleteClient = useCallback((id: string): Promise<void> =>
    withLoading(async () => {
      await axios.delete(CLIENT_ENDPOINTS.BY_ID(id), {
        headers: authHeader(),
      });
    }), [withLoading]);

  return { loading, error, fetchClientTypes, fetchClientsByListing, fetchClient, createClient, updateClient, deleteClient };
}


